import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { Clock, Globe, Users, Server, Calendar } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';

// Animated counter component
function formatNumber(num: number): string {
  return Math.floor(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function AnimatedCounter({ value, suffix, duration = 6000, decimals = 0, reverse = false, startFrom, noFormat = false }: { value: number; suffix: string; duration?: number; decimals?: number; reverse?: boolean; startFrom?: number; noFormat?: boolean }) {
  const [count, setCount] = useState(reverse ? (startFrom ?? value * 2) : 0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || shouldReduceMotion) return;

    let startTime: number;
    let animationFrame: number;
    const start = reverse ? (startFrom ?? value * 2) : 0;
    const end = value;
    
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      if (reverse) {
        setCount(start - (start - end) * easeOutQuart);
      } else {
        setCount(end * easeOutQuart);
      }
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      }
    };

    animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, value, duration, reverse, startFrom, shouldReduceMotion]);

  const displayedCount = shouldReduceMotion ? value : count;
  const displayValue = decimals > 0 ? displayedCount.toFixed(decimals) : (noFormat ? Math.floor(displayedCount).toString() : formatNumber(displayedCount));

  return (
    <div ref={ref} className="text-2xl sm:text-3xl md:text-4xl font-display font-bold bg-gradient-to-br from-primary via-primary to-secondary bg-clip-text text-transparent">
      {displayValue}{suffix}
    </div>
  );
}

export function TrustBar() {
  const { t } = useLanguage();

  const stats = [
    { value: 2012, suffix: '', label: t.stats.years, description: t.stats.yearsLabel, icon: Calendar, accentColor: 'text-primary', reverse: true, startFrom: 2026, noFormat: true },
    { value: 1000, suffix: '+', label: t.stats.countries, description: t.stats.countriesLabel, icon: Globe, accentColor: 'text-cyan-500' },
    { value: 3000, suffix: '+', label: t.stats.users, description: t.stats.usersLabel, icon: Users, accentColor: 'text-emerald-500' },
    { value: 99.99, suffix: '%', label: t.stats.uptime, description: t.stats.uptimeLabel, decimals: 2, icon: Clock, accentColor: 'text-amber-500' },
    { value: 3, suffix: '+', label: t.stats.data, description: t.stats.dataLabel, icon: Server, accentColor: 'text-violet-500', noFormat: true },
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-background relative">
      {/* Subtle transition from Hero */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-muted/30 to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Stats with icons - 5 columns */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 max-w-6xl mx-auto">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="text-center p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl bg-card border border-border/50 card-interactive group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Premium Icon */}
                <div className="relative w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 icon-float">
                  <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-muted/50 group-hover:bg-primary/5 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.accentColor} stroke-[1.5] transition-all duration-300`} />
                  </div>
                  <div className={`absolute -top-0.5 -right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${stat.accentColor.replace('text-', 'bg-')} opacity-50 group-hover:opacity-100 transition-opacity duration-300`} />
                </div>
                
                {/* Counter */}
                <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.decimals || 0} reverse={stat.reverse} startFrom={stat.startFrom} noFormat={stat.noFormat} />
                
                {/* Labels */}
                <p className="text-sm sm:text-base lg:text-lg font-semibold text-foreground mt-2 sm:mt-3 mb-0.5 sm:mb-1 group-hover:text-primary transition-colors duration-300">{stat.label}</p>
                <p className="text-[10px] sm:text-xs lg:text-sm text-muted-foreground leading-tight">{stat.description}</p>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Bottom transition */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-muted/20 pointer-events-none" />
    </section>
  );
}
